import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/use-api-client.js';

interface PastDueToastProps {
  onDismiss: () => void;
}

/**
 * Shown when /v1/status returns `quotaState: 'past_due'` — Stripe's
 * renewal charge failed and Smart Retries are in progress. Per Stripe's
 * subscription-overview guidance we keep access during this grace window;
 * the toast surfaces the failure so the user can update their card before
 * the subscription transitions to `unpaid` / `canceled` (which would
 * clear the cache and lock them out via PAYMENT_REQUIRED).
 *
 * The CTA opens the Stripe-hosted Customer Portal via POST /v1/billing/portal
 * — same flow as the existing "Manage Billing" button on the billing page.
 * Doing the POST + redirect here saves the user a click.
 */
export function PastDueToast({ onDismiss }: PastDueToastProps) {
  const { t } = useTranslation('dashboard');
  const { fetchApi } = useApiClient();
  const [loading, setLoading] = useState(false);

  async function openPortal() {
    setLoading(true);
    try {
      const res = await fetchApi('/v1/billing/portal', { method: 'POST' });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const body = (await res.json()) as { data?: { url?: string } };
      const url = body?.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="pr-8 text-h3 font-semibold leading-tight tracking-tight text-primary">
          {t('pastDue.title')}
        </p>
        <p className="text-sm leading-snug text-secondary">{t('pastDue.description')}</p>
      </div>
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onDismiss}
          data-variant="ghost"
          className="btn-press text-sm text-secondary hover:text-primary hover:underline transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('pastDue.dismiss')}
        </button>
        <button
          type="button"
          onClick={openPortal}
          disabled={loading}
          className="btn-press inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-lime px-4 py-2 text-sm font-semibold text-canvas transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {loading ? t('pastDue.opening') : t('pastDue.updatePayment')}
        </button>
      </div>
    </div>
  );
}
